import { useCallback, useRef, useState } from "react";
import { countChatTokens } from "~/helpers/tokenUtils";
import { apiFetch } from "~/lib/fetchClient";
import normalizeProduct from "../lib/normalizeProduct";

const CREATE_CONVERSATION_API = "/ai/create_conversation";
const CREATE_MESSAGE_API = "/ai/create_message";
const GET_TOKENS_USAGE = "/ai/tokens_usage";

const createSession = () => ({
  id: null,
  tokens: 0,
  saveQueue: Promise.resolve(),
});

const useConversationAPI = (messagesToPlain, messages, contextParams = {}) => {
  const sessionRef = useRef(createSession());

  const [conversationId, setConversationId] = useState(null);
  const [subscriptionLimit, setSubscriptionLimit] = useState(null);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const startConversation = async () => {
    const body = {};
    if (contextParams.leadId) {
      body.lead_id = contextParams.leadId;
    }
    if (contextParams.contextDepth) {
      body.context_depth = contextParams.contextDepth;
    }
    if (contextParams.editorType) {
      body.editor_type = contextParams.editorType;
    }
    const product = normalizeProduct(contextParams.product);
    if (product) {
      body.product = product;
    }

    const data =
      Object.keys(body).length > 0
        ? await apiFetch.post(CREATE_CONVERSATION_API, { json: body })
        : await apiFetch.post(CREATE_CONVERSATION_API);
    return data.id;
  };

  const ensureConversation = async () => {
    if (sessionRef.current.id) {
      return sessionRef.current.id;
    }

    const id = await startConversation();
    if (id) {
      setConversationId(id);
      sessionRef.current.id = id;
    }
    return id;
  };

  const saveUserMessage = async (content, tier) => {
    await apiFetch.post(CREATE_MESSAGE_API, {
      json: {
        role: "user",
        content,
        ai_conversation_id: sessionRef.current.id,
        model_tier: tier,
      },
    });
  };

  const saveAssistantMessage = async (
    plainMsg,
    allMessages,
    serverTokenCount,
    tier,
  ) => {
    const callTokens =
      serverTokenCount ??
      countChatTokens([...messagesToPlain(allMessages), plainMsg]);

    // The API stores a cumulative conversation total. Serialize partial and
    // fallback saves so an older response cannot overwrite a newer total.
    const session = sessionRef.current;
    const save = session.saveQueue.then(async () => {
      const nextTokens = session.tokens + callTokens;
      const data = await apiFetch.post(CREATE_MESSAGE_API, {
        json: {
          ...plainMsg,
          ai_conversation_id: session.id,
          tokens_count: nextTokens,
          model_tier: tier,
        },
      });
      session.tokens = nextTokens;
      setTotalTokensUsed((prev) => prev + callTokens);
      return data;
    });
    session.saveQueue = save.catch(() => undefined);
    return save;
  };

  const fetchTokensUsage = useCallback(async () => {
    setIsLoading(true);

    try {
      const data = await apiFetch.get(GET_TOKENS_USAGE);

      setSubscriptionLimit(data.subscription_limits);
      setTotalTokensUsed(data.tokens_count);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetConversation = () => {
    setConversationId(null);
    sessionRef.current = createSession();
  };

  return {
    conversationId,
    subscriptionLimit,
    totalTokensUsed,
    isLoading,
    ensureConversation,
    saveUserMessage,
    saveAssistantMessage,
    fetchTokensUsage,
    resetConversation,
  };
};

export default useConversationAPI;
